// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useMatchRoute } from "@tanstack/react-router";
import { ClipboardCheck, Home } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FocalLogoIcon } from "@/components/icons";
import { ChangelogDialog } from "@/components/layout/changelog-dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { releaseCodename } from "@/lib/changelog-data";
import { useDebugMode } from "@/lib/debug-mode";
import { NAV_GROUPS } from "@/lib/navigation";

// Inactive nav icons carry the accent (text-primary, which every accent theme swaps). On hover
// the button's text flips to sidebar-accent-foreground, but the icon's own color wins over
// inherited color — so in dark mode override it on hover so the icon matches the label.
// Light-mode hover keeps the accent.
const INACTIVE_ICON = "text-primary dark:group-hover/menu-item:text-sidebar-accent-foreground";

export function AppSidebar() {
  const { t } = useTranslation("back-office");
  const matchRoute = useMatchRoute();
  const { onLogoClick, debugMode } = useDebugMode();
  const { hasPermission } = useAuth();
  const { state } = useSidebar();
  const [showVersion, setShowVersion] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const codename = releaseCodename(__APP_VERSION__);

  useEffect(
    function toggleVersionVisibilityOnExpand() {
      if (state === "expanded") {
        const timer = setTimeout(() => setShowVersion(true), 200);
        return () => clearTimeout(timer);
      } else {
        setShowVersion(false);
      }
    },
    [state],
  );

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="h-14 justify-center">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              disableRipple
              className="cursor-default p-0! hover:bg-transparent active:bg-transparent hover:text-inherit active:text-inherit translate-x-1 group-data-[collapsible=icon]:translate-x-0 transition-transform duration-200 ease-linear"
            >
              <div
                className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary"
                onClick={onLogoClick}
              >
                <FocalLogoIcon width={18} height={18} className="text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="truncate text-base font-medium leading-none">{t("appTitle")}</span>
                {import.meta.env.VITE_APP_ENV && (
                  <span className="text-[10px] font-normal leading-none text-muted-foreground/60">
                    {import.meta.env.VITE_APP_ENV}
                  </span>
                )}
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        <SidebarGroup className="py-1">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={!!matchRoute({ to: "/home", fuzzy: true })}
                  tooltip={t("home.title")}
                >
                  <Link to="/home">
                    <Home className={!matchRoute({ to: "/home", fuzzy: true }) ? INACTIVE_ICON : ""} />
                    <span>{t("home.title")}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {debugMode && hasPermission(PAGE_PERMISSIONS.approvals) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={!!matchRoute({ to: "/approvals", fuzzy: true })}
                    tooltip={t("approvals.title")}
                  >
                    <Link to="/approvals">
                      <ClipboardCheck className={!matchRoute({ to: "/approvals", fuzzy: true }) ? INACTIVE_ICON : ""} />
                      <span>{t("approvals.title")}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter(
            (item) => (!item.debugOnly || debugMode) && hasPermission(item.permission),
          );
          if (visibleItems.length === 0) return null;
          return (
            <SidebarGroup key={group.labelKey} className="py-1">
              <SidebarGroupLabel>{t(group.labelKey)}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map((item) => {
                    const isActive = !!matchRoute({ to: item.to, fuzzy: true });
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton asChild isActive={isActive} tooltip={t(item.labelKey)}>
                          <Link to={item.to}>
                            <item.icon className={!isActive ? INACTIVE_ICON : ""} />
                            <span>{t(item.labelKey)}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter>
        <div className="flex items-center">
          {showVersion && (
            <button
              type="button"
              onClick={() => setChangelogOpen(true)}
              className="pl-2 text-xs text-foreground/30 transition-colors hover:text-foreground/60 cursor-pointer"
            >
              {`v${__APP_VERSION__}${codename ? ` ${codename}` : ""} (${__GIT_SHA__})`}
            </button>
          )}
          <SidebarTrigger className="ml-auto group-data-[state=collapsed]:mx-auto hover:bg-primary/10 hover:text-primary transition-none" />
        </div>
      </SidebarFooter>
      <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
    </Sidebar>
  );
}
